import { useState, useCallback, useRef, useEffect, useId } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  Dropdown,
  Option,
  Text,
  Link,
  Spinner,
  makeStyles,
  tokens,
  mergeClasses,
} from '@coreai/fluentui-react';
import { Document20Regular, DismissRegular, Folder20Regular, ErrorCircle16Regular } from '@fluentui/react-icons';

// --- Types ---

export interface UploadFile {
  file: File;
  id: string;
  description: string;
}

export interface AddFileDialogProps {
  open: boolean;
  existingHubs: string[];
  /** When set, the dialog skips hub selection and uploads directly to this hub. */
  targetHub?: string;
  /**
   * When true, indicates the parent mutation is in flight. Disables Cancel/Submit
   * and file controls and short-circuits handleSubmit re-entry, preventing
   * duplicate hub creation or duplicate uploads from double-clicks / repeated
   * Enter presses.
   */
  isSubmitting?: boolean;
  onSubmit: (hubName: string, files: File[], description?: string, fileDescriptions?: Record<string, string>) => void;
  onCancel: () => void;
}

// --- Constants ---

const MAX_FILE_SIZE_MB = 4;
const MAX_UPLOAD_SIZE_MB = 50;
const ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt', '.md', '.html'];

// --- Styles ---

const useStyles = makeStyles({
  surface: {
    maxWidth: '560px',
    width: '100%',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    color: tokens.colorNeutralForeground3,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  hubField: {
    marginBlockEnd: tokens.spacingVerticalM,
  },
  dropdown: {
    width: '100%',
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXXL,
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transitionProperty: 'border-color, background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    minHeight: '160px',
  },
  dropZoneActive: {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: tokens.colorBrandStroke1,
    outlineOffset: '-2px',
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  dropZoneIcon: {
    fontSize: tokens.fontSizeHero800,
    color: tokens.colorPaletteYellowForeground2,
  },
  dropZoneText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
  },
  browseLink: {
    fontSize: tokens.fontSizeBase200,
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBlockStart: tokens.spacingVerticalM,
    maxHeight: '160px',
    overflow: 'auto',
  },
  fileItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  fileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fileDescription: {
    paddingInlineStart: '28px',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    overflow: 'hidden',
  },
  fileIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  fileName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  fileSize: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  removeBtn: {
    flexShrink: 0,
  },
  error: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    marginBlockStart: tokens.spacingVerticalXS,
  },
  errorIcon: {
    flexShrink: 0,
    marginTop: tokens.spacingVerticalXXS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
  hiddenInput: {
    display: 'none',
  },
});

// --- Helpers ---

const hubNameRegex = /^[a-zA-Z0-9_-]+$/;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

// --- Component ---

export const AddFileDialog = ({ open, existingHubs, targetHub, isSubmitting = false, onSubmit, onCancel }: AddFileDialogProps) => {
  const styles = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const errorId = useId();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [hubSelection, setHubSelection] = useState<'new' | 'existing'>('new');
  const [newHubName, setNewHubName] = useState('');
  const [selectedHub, setSelectedHub] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [description, setDescription] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFiles([]);
      if (targetHub) {
        setHubSelection('existing');
        setSelectedHub(targetHub);
      } else {
        setHubSelection(existingHubs.length > 0 ? 'existing' : 'new');
        setSelectedHub(existingHubs[0] ?? '');
      }
      setNewHubName('');
      setDragActive(false);
      setError('');
      setDescription('');
    }
  }, [open, existingHubs, targetHub]);

  const validateAndAddFiles = useCallback(
    (incoming: FileList | File[]) => {
      const newFiles: UploadFile[] = [];
      const totalExisting = files.reduce((sum, f) => sum + f.file.size, 0);
      let totalNew = 0;

      // Build a set of existing filenames for duplicate detection
      const existingNames = new Set(files.map((f) => f.file.name));
      const incomingNames = new Set<string>();

      for (const file of Array.from(incoming)) {
        // Check for duplicates against existing files
        if (existingNames.has(file.name)) {
          setError(`"${file.name}" is already in the upload list.`);
          return;
        }
        // Check for duplicates within the incoming batch
        if (incomingNames.has(file.name)) {
          setError(`"${file.name}" appears multiple times in the selection.`);
          return;
        }
        incomingNames.add(file.name);

        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!ACCEPTED_EXTENSIONS.includes(ext)) {
          setError(`Unsupported file type: ${ext}. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`);
          return;
        }
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          setError(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB per-file limit.`);
          return;
        }
        totalNew += file.size;
        if (totalExisting + totalNew > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
          setError(`Total upload size would exceed ${MAX_UPLOAD_SIZE_MB} MB.`);
          return;
        }
        newFiles.push({ file, id: generateId(), description: '' });
      }

      setError('');
      setFiles((prev) => [...prev, ...newFiles]);
    },
    [files]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files.length > 0) {
        validateAndAddFiles(e.dataTransfer.files);
      }
    },
    [validateAndAddFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleBrowse = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        validateAndAddFiles(e.target.files);
      }
      // Reset input so re-selecting the same file triggers onChange
      e.target.value = '';
    },
    [validateAndAddFiles]
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setError('');
  }, []);

  const hubName = targetHub ?? (hubSelection === 'new' ? newHubName.trim() : selectedHub);
  const isInvalidNewHubName = !targetHub && hubSelection === 'new' && !hubNameRegex.test(hubName);
  const canSubmit = files.length > 0 && hubName.length > 0 && !error && !isInvalidNewHubName;

  const updateFileDescription = useCallback((id: string, desc: string) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, description: desc } : f)));
  }, []);

  const handleSubmit = useCallback(() => {
    // Re-entry guard: if a parent mutation is already in flight, ignore extra
    // clicks / Enter presses so we don't create duplicate hubs or upload twice.
    if (isSubmitting) return;
    if (!canSubmit || isInvalidNewHubName) return;
    const fileDescriptions: Record<string, string> = {};
    for (const f of files) {
      const trimmed = f.description.trim();
      if (trimmed) fileDescriptions[f.file.name] = trimmed;
    }
    onSubmit(
      hubName,
      files.map((f) => f.file),
      description.trim() || undefined,
      Object.keys(fileDescriptions).length > 0 ? fileDescriptions : undefined
    );
  }, [canSubmit, hubName, files, isInvalidNewHubName, isSubmitting, onSubmit, description]);

  // Don't close while the parent mutation is in flight — otherwise a user can
  // dismiss the dialog mid-request and lose visibility into the outcome.
  const handleCancel = useCallback(() => {
    if (isSubmitting) return;
    onCancel();
  }, [isSubmitting, onCancel]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) handleCancel(); }}>
      <DialogSurface className={styles.surface}>
        <DialogTitle>
          <div className={styles.titleRow}>
            <Document20Regular className={styles.titleIcon} />
            <span>{targetHub ? `Upload to ${targetHub}` : 'Add file'}</span>
          </div>
        </DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text className={styles.subtitle}>
              Each file can be up to {MAX_FILE_SIZE_MB} MB, with a maximum of {MAX_UPLOAD_SIZE_MB} MB per upload.
            </Text>

            {/* Knowledge Hub selector — hidden when targeting a specific hub */}
            {!targetHub && (
              <Field
                className={styles.hubField}
                label="Knowledge Hub"
                hint={hubSelection === 'new' ? 'Files will be added to a new Knowledge Hub' : 'Files will be added to the selected Knowledge Hub'}
              >
                <Dropdown
                  className={styles.dropdown}
                  size="medium"
                  placeholder="Select or create a Knowledge Hub"
                  disabled={isSubmitting}
                  value={hubSelection === 'new' ? (newHubName || 'Create new...') : selectedHub}
                  selectedOptions={hubSelection === 'new' ? ['__new__'] : [selectedHub]}
                  onOptionSelect={(_, data) => {
                    if (data.optionValue === '__new__') {
                      setHubSelection('new');
                      setSelectedHub('');
                    } else if (data.optionValue) {
                      setHubSelection('existing');
                      setSelectedHub(data.optionValue);
                    }
                  }}
                >
                  <Option value="__new__">Create new...</Option>
                  {existingHubs.map((hub) => (
                    <Option key={hub} value={hub}>
                      {hub}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}

            {!targetHub && hubSelection === 'new' && (() => {
              const trimmedNewHubName = newHubName.trim();
              const isInvalidNewHubName = !!trimmedNewHubName && !hubNameRegex.test(trimmedNewHubName);

              return (
                <>
                <Field
                  className={styles.hubField}
                  label="Hub name"
                  {...(isInvalidNewHubName
                    ? { validationMessage: 'Use only letters, numbers, hyphens, and underscores', validationState: 'error' as const }
                    : {})}
                >
                  <Input
                    placeholder="e.g., HRPolicies"
                    value={newHubName}
                    required
                    disabled={isSubmitting}
                    pattern={hubNameRegex.source}
                    onChange={(_, data) => setNewHubName(data.value)}
                  />
                </Field>
                <Field
                  className={styles.hubField}
                  label="Description"
                  hint="Optional — describe what this knowledge hub contains"
                >
                  <Input
                    placeholder="e.g., Company HR policies and guidelines"
                    value={description}
                    disabled={isSubmitting}
                    onChange={(_, data) => setDescription(data.value)}
                  />
                </Field>
                </>
              );
            })()}
            {/* Drop zone */}
            <div
              className={mergeClasses(styles.dropZone, dragActive && styles.dropZoneActive)}
              onDrop={isSubmitting ? undefined : handleDrop}
              onDragOver={isSubmitting ? undefined : handleDragOver}
              onDragLeave={isSubmitting ? undefined : handleDragLeave}
              onClick={isSubmitting ? undefined : handleBrowse}
              role="button"
              tabIndex={isSubmitting ? -1 : 0}
              aria-disabled={isSubmitting || undefined}
              onKeyDown={(e) => {
                if (isSubmitting) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBrowse();
                }
              }}
              aria-label="Drop files here or click to browse for files"
              {...(error ? { 'aria-describedby': errorId } : {})}
            >
              <Folder20Regular className={styles.dropZoneIcon} />
              <Text className={styles.dropZoneText}>
                Drag files here, or{' '}
                <Link as="span" className={styles.browseLink} inline>
                  browse for files
                </Link>
              </Text>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_EXTENSIONS.join(',')}
              className={styles.hiddenInput}
              disabled={isSubmitting}
              onChange={handleFileInput}
              {...(error ? { 'aria-describedby': errorId } : {})}
            />

            {/*
              Validation errors are surfaced to assistive tech via a live region
              and linked to the drop-zone + hidden file input with aria-describedby
              (WCAG 3.3.1 Error Identification, 4.1.3 Status Messages). An icon
              accompanies the red text so the meaning isn't communicated by color
              alone (WCAG 1.4.1 Use of Color).
            */}
            <div id={errorId} role="alert" aria-live="assertive">
              {error && (
                <Text className={styles.error}>
                  <ErrorCircle16Regular className={styles.errorIcon} aria-hidden="true" />
                  <span>{error}</span>
                </Text>
              )}
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className={styles.fileList}>
                {files.map((f) => (
                  <div key={f.id} className={styles.fileItem}>
                    <div className={styles.fileHeader}>
                      <div className={styles.fileInfo}>
                        <Document20Regular className={styles.fileIcon} />
                        <Text className={styles.fileName}>{f.file.name}</Text>
                      </div>
                      <div className={styles.fileInfo}>
                        <Text className={styles.fileSize}>{formatFileSize(f.file.size)}</Text>
                        <Button
                          className={styles.removeBtn}
                          appearance="subtle"
                          size="small"
                          icon={<DismissRegular />}
                          onClick={() => removeFile(f.id)}
                          disabled={isSubmitting}
                          aria-label={`Remove ${f.file.name}`}
                        />
                      </div>
                    </div>
                    <Input
                      className={styles.fileDescription}
                      size="small"
                      placeholder="Description (optional)"
                      aria-label={`Description for ${f.file.name}`}
                      value={f.description}
                      disabled={isSubmitting}
                      onChange={(_, data) => updateFileDescription(f.id, data.value)}
                    />
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button appearance="secondary" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canSubmit || isSubmitting}
              onClick={handleSubmit}
              {...(isSubmitting ? { icon: <Spinner size="tiny" /> } : {})}
            >
              {isSubmitting ? 'Uploading…' : targetHub ? 'Upload' : 'Add file'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
